import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { HardHat, Mail, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (res.ok) {
        login(data.user, data.access_token);
        toast.success(language === "sv" ? "Inloggad!" : "Logged in!");
        navigate("/dashboard");
      } else {
        toast.error(data.detail || (language === "sv" ? "Inloggning misslyckades" : "Login failed"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
      style={{ 
        backgroundImage: "url('https://images.unsplash.com/photo-1644657711115-ee46e8dd7c7d?w=1920&q=80')"
      }}
    >
      <div className="absolute inset-0 bg-slate-900/60"></div>
      
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          {language === "sv" ? "Tillbaka" : "Back"}
        </Link>

        <Card className="shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <HardHat className="w-10 h-10 text-primary" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Logga in" : "Sign in"}
            </CardTitle>
            <CardDescription>
              {language === "sv" 
                ? "Välkommen tillbaka till Controlify" 
                : "Welcome back to Controlify"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              variant="outline" 
              className="w-full h-12" 
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {language === "sv" ? "Fortsätt med Google" : "Continue with Google"}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">
                  {language === "sv" ? "eller" : "or"}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="form-label">
                  {language === "sv" ? "E-post" : "Email"}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="namn@foretag.se"
                    className="pl-10 h-12"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="form-label">
                  {language === "sv" ? "Lösenord" : "Password"}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-12"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12" 
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading 
                  ? (language === "sv" ? "Loggar in..." : "Signing in...") 
                  : (language === "sv" ? "Logga in" : "Sign in")}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-600">
              {language === "sv" ? "Har du inget konto?" : "Don't have an account?"}{" "}
              <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
                {language === "sv" ? "Registrera dig" : "Sign up"}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
